import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';
import {InputComponent, IInputComponentConfiguration} from '../Input/Input';

export interface IInputNumberComponentConfiguration extends IInputComponentConfiguration<
	number,
	number
> {
	accuracity?: number;
	step?: number;
	//use min or getMin
	min?: number;
	getMin?: CM.PropertyOperation.TDescriptor;
	//use max or getMax
	max?: number;
	getMax?: CM.PropertyOperation.TDescriptor;
	debouncerDelay?: number | null;
	roundingMode?: 'closest' | 'up' | 'down';
	/**
	 * Id of a standard rendering mode or path to an instance of InputNumberRenderingMode
	 * Default = 'textual'
	 */
	renderingMode?: (
		'slider' |
		'textual' |
		'textualWithIncrement' |
		CM.PropertyOperation.TDescriptor
		);
};
/**
 * Interface designed for allowing to have only one input-number class
 * that can have many different representations such as a textual input number or a slider.
 * The rendering mode is responsible for DOM generation / events dispatching etc...
 **/
export type TInputNumberRenderingMode = CM.Plugin.TPlugin<
	InputNumberComponent,
	{
		/**
		 * Called when min or max updated, stepsCountDiff = oldStepsCount - newStepsCounts
		 **/
		boundsUpdated(component: InputNumberComponent, stepsCountDiff: number): void | Promise<void>;
	}
>;

export class InputNumberComponent extends InputComponent<
	HTMLDivElement,
	number,
	number,
	InputNumberComponent,
	NS.Configuration
> {
	static readonly standardRenderingModes: {
		slider: SliderRenderingMode;
		textual: TextualRenderingMode;
		textualWithIncrement: TextualWithIncrementRenderingMode;
	} = <any>{};
	static count = 0;

	static override initialize() {
		InputComponent.initialize.call(this);
		this.standardRenderingModes.slider = new SliderRenderingMode;
		this.standardRenderingModes.textual = new TextualRenderingMode;
		this.standardRenderingModes.textualWithIncrement = new TextualWithIncrementRenderingMode;
		this.declareEventType('dragging');
		this.declareEventType('draggend');
	};

	renderingMode!: TInputNumberRenderingMode;
	component_DOM: HTMLDivElement;
	step: number;
	min: number;
	max: number;
	getMin?: CM.PropertyOperation.TOperation;
	getMax?: CM.PropertyOperation.TOperation;
	stepsCount: number = 1;
	roundingMode: 'closest' | 'up' | 'down';
	isFirstBoundUpdateDone = false;

	override async component_initialize() {
		if (this.component_configuration.renderingMode && (typeof this.component_configuration.renderingMode !== 'string')) {
			const getRM = CM.PropertyOperation.create(
				this.component_configuration.renderingMode
			);
			const RM = await getRM.execute(this, this.component_getEventWhichRepresentNoEvent());
			if (!(RM instanceof CM.Plugin)) {
				this.component_throwUserError('Invalid rendering mode', {
					renderingModePropertyPathValueGetter: getRM
				});
			}
			this.renderingMode = <any>RM;
		} else {
			this.renderingMode =
				InputNumberComponent.standardRenderingModes[this.component_configuration.renderingMode ?? 'textual'];
		}
		await super.component_initialize();
	}

	override isValueTheDefault() {
		return (this.value === this.defaultValue);
	};

	override deserialize(x: number) {
		return (x);
	};

	override async component_start(startEvent: STD.events.Event<any, CM.Component<any, any>>) {
		if (this.component_configuration.getMin) {
			this.getMin = CM.PropertyOperation.create(
				this.component_configuration.getMin
			);
			const component = this.getMin.getComponent(this);
			if (component && component.component_hasValue()) {
				component.addEventListener('value', this.updateConstraints.bind(this), {
					listener: this,
					subType: '*'
				});
			}
		}
		if (this.component_configuration.getMax) {
			this.getMax = CM.PropertyOperation.create(
				this.component_configuration.getMax
			);
			const component = this.getMax.getComponent(this);
			if (component && component.component_hasValue()) {
				component.addEventListener('value', this.updateConstraints.bind(this), {
					listener: this,
					subType: '*'
				});
			}
		}
		this.renderingMode.attach(this);
		await super.component_start(startEvent);
		// if (!this.getMin && !this.getMax) {
		await this.updateConstraints(startEvent);
		// }
	};

	private constraintsUpdatePrevented = false;

	async updateConstraints(
		event: STD.events.Event<any, CM.Component<any, any>> = this.component_getEventWhichRepresentNoEvent()
	) {
		// if (!this.component_isStarted || this.component_isDisabled) {
		// 	return;
		// }
		if (this.component_isDisabled) {
			this.constraintsUpdatePrevented = true;
			return;
		}
		this.constraintsUpdatePrevented = false;
		let boundsTouched = false;
		if (this.getMin) {
			let min = await this.getMin.execute(this, event);
			try {
				min = STD.math.roundToStep(min, this.step);
			} catch (error: any) {
				this.component_throwBoundError('Failed to round min', error, {min});
			}
			if (this.min !== min) {
				boundsTouched = true;
				this.min = min;
				if (this.min === -Infinity || (this.min === Infinity)) {
					this.component_throwUserError(
						'Minimum value should not be infinite!',
						{min}
					);
				}
			}
		}
		if (this.getMax) {
			let max = await this.getMax.execute(this, event);
			try {
				console.log('!!!', {
					comp: this,
					max,
					step: this.step,
					roundingMode: this.roundingMode
				});
				max = STD.math.roundToStep(max, this.step, this.roundingMode);
			} catch (error: any) {
				this.component_throwBoundError('Failed to round max', error, {max});
			}
			if (this.max !== max) {
				boundsTouched = true;
				this.max = max;
				if (this.max === -Infinity || this.max === Infinity) {
					this.component_throwUserError(
						'Maximum value should not be infinite!',
						{max}
					);
				}
			}
		}
		if (boundsTouched && this.min > this.max) {
			this.component_throwUserError(
				'Minimum value cannot be > to maximum value',
				{min: this.min, max: this.max}
			);
		}
		if (boundsTouched || !this.isFirstBoundUpdateDone) {
			await this.updateValue(this.value, undefined, undefined, true);
			const stepsCount = this.step ?
				Math.floor((this.max - this.min) / this.step) :
				0
			;
			if (stepsCount !== this.stepsCount || !this.isFirstBoundUpdateDone) {
				await this.renderingMode.boundsUpdated(this, this.stepsCount - stepsCount);
				this.stepsCount = stepsCount;
			}
			this.isFirstBoundUpdateDone = true;
		}
	};

	applyConstraints(value: number | null) {
		if (value === null) {
			this.component_throwLibraryError('Value cannot be null', {});
		}

		if (Number.isNaN(value)) {
			this.component_throwLibraryError('Value cannot be NaN', {});
		}

		try {
			value = STD.math.roundToStep(value, this.step, this.component_configuration.roundingMode);
		} catch (error: any) {
			if (error.details?.type === STD.math.unsafeIntegerSymbol) {
				value = STD.math.closest(value, [this.min, this.max]);
			} else {
				this.component_throwBoundError('Failed to round value', error, {});
			}
		}

		if (value < this.min) {
			value = this.min;
		} else if (value > this.max) {
			value = this.max;
		}

		return value;
	}

	isValidValue(value: number) {
		let isValid = (
			!Number.isNaN(value) &&
			value >= this.min &&
			value <= this.max
		);

		if (!isValid) return false;

		try {
			isValid = STD.math.mod(value, this.step) === 0;
		} catch (error: any) {
			if ([STD.math.unsafeIntegerSymbol, STD.math.exceededMaxPrecisionFactor].includes(error.details?.type)) {
				isValid = false;
			} else {
				this.component_throwBoundError('Failed to validate value', error, {});
			}
		}

		return isValid;
	};

	override async updateValue(newValue: number | null, _defaultValueMarker?: Symbol, force?: boolean, noValidation?: boolean) {
		if (noValidation !== true) {
			newValue = this.applyConstraints(newValue);
		}
		await super.updateValue(newValue, _defaultValueMarker, force);
	};

	async reset() {
		await this.updateValue(this.component_configuration.defaultValue || null);
	};

	serialize() {
		return this.value;
	};

	override async component_onInternalActivation(event: STD.events.Event<any, CM.Component<any, any>>) {
		await super.component_onInternalActivation(event);
		if (!this.component_isDisabled && this.constraintsUpdatePrevented) {
			await this.updateConstraints(event);
		}
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: NS.Configuration
	) {
		super(descriptor, Object.assign(
			{
				defaultValue: 0,
				accuracity: 2,
				step: 1
			},
			configuration
		));
		if (
			(<IInputNumberComponentConfiguration>descriptor).defaultValue &&
			(typeof (<IInputNumberComponentConfiguration>descriptor).defaultValue !== 'number')
		) {
			throw new Error;
		}
		this.roundingMode = this.component_configuration.roundingMode ?? 'closest';
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({});
		this.step = <number>this.component_configuration.step;
		try {
			this.min = STD.math.roundToStep(this.component_configuration.min ?? 0, this.step, this.roundingMode);
			this.max = STD.math.roundToStep(this.component_configuration.max ?? 100, this.step, this.roundingMode);
		} catch (error: any) {
			this.min = 0;
			this.max = 0;
			this.component_throwBoundError('Failed to round min or max', error, {});
		}
	};
};
export namespace NS {
	export const Component = InputNumberComponent;
	export type Component = InputNumberComponent;
	export type Configuration = IInputNumberComponentConfiguration;
	export type RenderingMode = TInputNumberRenderingMode;
}
CM.Manager.declare({
	id: 'input-number',
	type: InputNumberComponent
});
import SliderRenderingMode from './renderingModes/Slider';
import {TextualRenderingMode} from './renderingModes/Textual';
import {TextualWithIncrementRenderingMode} from './renderingModes/TextualWithIncrement';
